import type { OrderDetails } from '@/entities/order/model/order.types'
import type { StateDefinition } from '@/entities/state/model/state.types'
import { getExpectedCrateItemsForZebraOrder } from './order.crate'
import { getZebraOrderScanState } from './zebraScanStorage'

export const ON_THE_WAY_CATEGORY_TITLE = 'On The Way'

export const CORE_ON_THE_WAY_STATUS_NAMES = [
  'OnTheWay',
  'TempoOnTheWay',
  'PartnerOnTheWay',
] as const

export const ARRIVED_STATUS = 'Arrived'

export const DELIVERING_TO_CUSTOMER_STATUS = 'DeliveringToCustomer'

export const COMPLETED_STATUS = 'Completed'

export const READY_TO_PICK_STATUS = 'ReadyToPick'

export const READY_TO_COLLECT_STATUS = 'ReadyToCollect'

/** BD-8588: shown in Order Details while array_barcodes has not been received. */
export const LICENCE_PLATE_WAITING_MESSAGE = 'Close the wave to see licence plate details'

const DISCARD_STATUS_NAMES = new Set([
  'completed',
  'cancelled',
  'fulfilled',
])

export function getOnTheWayMinProgressionLevel(states: StateDefinition[] | undefined): number {
  if (!states?.length) {
    return Number.POSITIVE_INFINITY
  }

  const levels = CORE_ON_THE_WAY_STATUS_NAMES
    .map(name => states.find(s => s.name === name)?.progressionLevel)
    .filter((level): level is number => typeof level === 'number')

  return levels.length > 0 ? Math.min(...levels) : Number.POSITIVE_INFINITY
}

/**
 * Whether scan progress UI should be shown from On The Way through pre-completion.
 */
export function isZebraScanProgressStatus(
  status: string | undefined,
  states: StateDefinition[] | undefined,
): boolean {
  if (!status || !states?.length) {
    return false
  }

  if (DISCARD_STATUS_NAMES.has(status.toLowerCase())) {
    return false
  }

  const state = states.find(s => s.name === status)
  if (!state) {
    return false
  }

  if (
    status === READY_TO_PICK_STATUS
    || status === READY_TO_COLLECT_STATUS
    || status === ARRIVED_STATUS
    || status === DELIVERING_TO_CUSTOMER_STATUS
  ) {
    return true
  }

  return state.displayState?.categoryTitle === ON_THE_WAY_CATEGORY_TITLE
}

/**
 * Zebra scan-progress statuses strictly before Arrived (BD-8588 waiting card).
 * ReadyToPick, ReadyToCollect, and all On The Way category states — not Arrived / Delivering.
 */
export function isBeforeArrivedZebraScanStatus(
  status: string | undefined,
  states: StateDefinition[] | undefined,
): boolean {
  if (!status || status === ARRIVED_STATUS || status === DELIVERING_TO_CUSTOMER_STATUS) {
    return false
  }
  return isZebraScanProgressStatus(status, states)
}

/**
 * Whether Order Details should show the licence-plate waiting card (BD-8588).
 * Requires zebraBarcodeEnabled, a pre-Arrived scan-progress status, and no array_barcodes.
 */
export function shouldShowLicencePlateWaitingCard({
  zebraBarcodeEnabled,
  status,
  states,
  hasArrayBarcodes,
  isCancelled = false,
}: {
  zebraBarcodeEnabled: boolean
  status: string | undefined
  states: StateDefinition[] | undefined
  hasArrayBarcodes: boolean
  isCancelled?: boolean
}): boolean {
  if (!zebraBarcodeEnabled || isCancelled || hasArrayBarcodes) {
    return false
  }
  return isBeforeArrivedZebraScanStatus(status, states)
}

/** How long expected crates cached on this device stand in for a payload that lacks them. */
export const EXPECTED_CRATES_CACHE_TTL_MS = 15 * 60 * 1000

/**
 * Whether the order's licence plates (array_barcodes) have been received (BD-8588/8589/8590).
 * Besides array_barcodes itself, crateScan.expectedBarcodes and the expected crates cached on
 * this device only exist once barcodes were received. They also cover the minutes after
 * Arrived -> Delivering when the payload can arrive without barcode data.
 */
export function hasReceivedLicencePlates(order: OrderDetails | undefined): boolean {
  if (getExpectedCrateItemsForZebraOrder(order).length > 0) {
    return true
  }
  if (!order?.orderId) {
    return false
  }
  const persisted = getZebraOrderScanState(order.orderId)
  if (!persisted?.expectedCrateItems?.length) {
    return false
  }
  const updatedAtMs = Date.parse(persisted.updatedAt)
  return !Number.isNaN(updatedAtMs) && Date.now() - updatedAtMs <= EXPECTED_CRATES_CACHE_TTL_MS
}

/** BD-8589: shown before completing an order that never received array_barcodes. */
export const NO_BARCODE_COMPLETION_MESSAGE = 'Completing the order now does not guarantee it has been delivered in full.'

/**
 * Whether completing these orders must be confirmed first (BD-8589).
 * Every device on a Zebra-enabled destination (BD-8590): iOS, desktop and non-Zebra Android
 * can complete the order too, so they are warned the same way.
 */
export function requiresNoBarcodeCompletionConfirm({
  zebraBarcodeEnabled,
  action,
  orders,
}: {
  zebraBarcodeEnabled: boolean
  action: string
  orders: OrderDetails[]
}): boolean {
  if (action !== 'Completed' || !zebraBarcodeEnabled) {
    return false
  }
  return orders.some(order => !hasReceivedLicencePlates(order))
}

/** BD-8590: the order card's button for an Arrived order without licence plates. */
export const PROCEED_CARD_LABEL = 'Proceed'

/**
 * The order card's action while the order has no licence plates (BD-8590).
 * - Arrived: "Proceed", which opens Order Details, where "Proceed without scanning" lives.
 * - Delivering to Customer: the configured quick button, confirmed by BD-8589 on Complete.
 * - Earlier states: no button, as there is nothing to scan yet.
 */
export function getNoLicencePlateCardAction(status: string | undefined): 'proceed' | 'primary' | 'none' {
  if (status === ARRIVED_STATUS) {
    return 'proceed'
  }
  return status === DELIVERING_TO_CUSTOMER_STATUS ? 'primary' : 'none'
}

/** BD-8590: shown in Order Details for an order completed without scanning (spec UI/UX table). */
export const COMPLETED_WITHOUT_SCANNING_MESSAGE = 'Need to mark the order completed without checking all crates were included?'

/**
 * Whether a Completed order was completed without scanning (BD-8590).
 *
 * No backend flag exists yet, so this is read from the data: the destination has Zebra
 * scanning on, the order never received licence plates, and no crate was ever scanned or
 * marked missing. Zebra-off destinations keep their hsFields crate rows unchanged.
 */
export function isCompletedWithoutScanning(
  order: OrderDetails | undefined,
  zebraBarcodeEnabled: boolean,
): boolean {
  if (!zebraBarcodeEnabled || order?.status !== COMPLETED_STATUS) {
    return false
  }
  const hasScanActivity = (order.crateScan?.scannedBarcodes?.length ?? 0) > 0
    || (order.crateScan?.missingCrates?.length ?? 0) > 0
  return !hasScanActivity && !hasReceivedLicencePlates(order)
}

/**
 * Clear local scan state when order falls outside the active Zebra progress window.
 */
export function shouldDiscardZebraScanState(
  status: string | undefined,
  states: StateDefinition[] | undefined,
): boolean {
  if (!status) {
    return true
  }

  if (DISCARD_STATUS_NAMES.has(status.toLowerCase())) {
    return true
  }

  if (!states?.length) {
    return false
  }

  return !isZebraScanProgressStatus(status, states)
}

/**
 * Returns true when every expected crate has been either scanned or its type marked as missing.
 * The counter numerator still shows scanned-only; this drives the "We're on it" label flip.
 */
export function isAllCratesProcessed(order: OrderDetails | undefined): boolean {
  const expected = getExpectedCrateItemsForZebraOrder(order)
  if (expected.length === 0) {
    return false
  }

  const scannedBarcodes = new Set(
    (order?.crateScan?.scannedBarcodes ?? []).map(i => i.barcode),
  )
  const missingCrateTypes = new Set(
    (order?.crateScan?.missingCrates ?? []).map(c => c.crateType),
  )

  return expected.every(
    item => scannedBarcodes.has(item.barcode) || missingCrateTypes.has(item.crateType),
  )
}

/**
 * Base label for a group's action button (BD-8186), before the `(n)` order-count suffix
 * that the group cards already append. Mirrors the single-order flow in StatusOrderCard:
 * progress while crates are outstanding, the configured quick-button label once the group
 * is ready in Arrived / DeliveringToCustomer, and `Scanned x/x` for the statuses between.
 */
export function getGroupScanActionLabel(
  scanState: { showScanProgress: boolean, scanned: number, total: number, allProcessed: boolean },
  status: string | undefined,
  primaryActionLabel: string | undefined,
): string | undefined {
  const { showScanProgress, scanned, total, allProcessed } = scanState

  if (!showScanProgress) {
    return primaryActionLabel
  }

  if (allProcessed) {
    // BD-8585: a group carrying no crate metadata is counted as processed so it cannot
    // deadlock the transition gate (see isGroupAllCratesProcessed). There was nothing to
    // scan, so it must not claim `Scanned 0/0` - it falls back to the configured label,
    // as a single order does through its own `total === 0` guard.
    const hasNothingToScan = total === 0
    const isReadyToTransition = status === ARRIVED_STATUS || status === DELIVERING_TO_CUSTOMER_STATUS

    return hasNothingToScan || isReadyToTransition
      ? primaryActionLabel
      : `Scanned ${total}/${total}`
  }

  return scanned > 0
    ? `Continue scanning ${scanned}/${total}`
    : `Scan ${scanned}/${total}`
}

/**
 * Combined scan progress across every order in a group (BD-8186).
 * The numerator counts scanned barcodes only, matching the single-order counter.
 */
export function getGroupScanProgressCounts(orders: OrderDetails[]): {
  scanned: number
  total: number
} {
  return orders.reduce((totals, order) => {
    const { scanned, total } = getDeviceScanProgressCounts(order)
    return { scanned: totals.scanned + scanned, total: totals.total + total }
  }, { scanned: 0, total: 0 })
}

/**
 * Whether every order in a group has all of its crates accounted for (BD-8186).
 *
 * Mirrors the single-order completion gate: a crate counts as processed when scanned
 * OR its type is marked missing, so a genuinely missing crate cannot deadlock the group.
 * An order carrying no expected crate metadata is treated as processed for the same
 * reason - `isAllCratesProcessed` returns false for it, which would otherwise block the
 * whole group forever.
 */
export function isGroupAllCratesProcessed(orders: OrderDetails[]): boolean {
  if (orders.length === 0) {
    return false
  }

  return orders.every(order => isOrderAccountedForGroupScan(order))
}

/**
 * Everything this device knows was scanned or marked missing for an order: what the
 * server has echoed back, plus what was recorded here and has not round-tripped yet.
 *
 * Order Details already decides "complete" from exactly this union. The group helpers
 * have to use the same definition, because the two sources genuinely diverge on a
 * handset: the board renders orders from the realtime store, while the offline
 * reconcile only ever merges local scans into the polled query cache - and polling is
 * switched off while realtime is connected. Reading `order.crateScan` alone therefore
 * leaves the board believing a member is unscanned while the screen for that member
 * shows it finished (BD-8185 Scenario 2).
 */
function getDeviceKnownScanState(order: OrderDetails | undefined): {
  scannedBarcodes: Set<string>
  missingCrateTypes: Set<string>
} {
  const persisted = order?.orderId ? getZebraOrderScanState(order.orderId) : null

  const scannedBarcodes = new Set<string>()
  for (const item of [...(order?.crateScan?.scannedBarcodes ?? []), ...(persisted?.scannedBarcodes ?? [])]) {
    scannedBarcodes.add(item.barcode)
  }

  const missingCrateTypes = new Set<string>()
  for (const crate of [...(order?.crateScan?.missingCrates ?? []), ...(persisted?.missingCrates ?? [])]) {
    missingCrateTypes.add(crate.crateType)
  }

  return { scannedBarcodes, missingCrateTypes }
}

/**
 * Scan progress for one group member, counting what this device knows (BD-8185).
 * The single-order counter deliberately stays server-only; this one has to agree with
 * Order Details so the group card cannot promise scanning that is already done.
 */
export function getDeviceScanProgressCounts(order: OrderDetails | undefined): {
  scanned: number
  total: number
} {
  const expected = getExpectedCrateItemsForZebraOrder(order)
  const total = expected.length

  if (total === 0) {
    return { scanned: 0, total: 0 }
  }

  const { scannedBarcodes } = getDeviceKnownScanState(order)
  const scanned = expected.filter(item => scannedBarcodes.has(item.barcode)).length

  return { scanned, total }
}

/**
 * Whether a single group member is fully accounted for (BD-8185 / BD-8186).
 * No expected crates, or every crate scanned / marked missing on this device.
 */
export function isOrderAccountedForGroupScan(order: OrderDetails): boolean {
  const expected = getExpectedCrateItemsForZebraOrder(order)
  if (expected.length === 0) {
    return true
  }

  const { scannedBarcodes, missingCrateTypes } = getDeviceKnownScanState(order)
  return expected.every(
    item => scannedBarcodes.has(item.barcode) || missingCrateTypes.has(item.crateType),
  )
}

/**
 * Next group member that still needs scanning (BD-8185).
 *
 * - With no `afterOrderId`, returns the first incomplete member in group order
 *   (Entry point A: open the first / remaining unscanned order).
 * - With `afterOrderId`, returns the next incomplete member after that id,
 *   wrapping around the group (auto-advance / "Go to next grouped order").
 */
export function getNextIncompleteGroupOrderId(
  orders: OrderDetails[],
  afterOrderId?: string,
): string | undefined {
  if (orders.length === 0) {
    return undefined
  }

  const incomplete = orders.filter(order => !isOrderAccountedForGroupScan(order))
  if (incomplete.length === 0) {
    return undefined
  }

  if (!afterOrderId) {
    return incomplete[0]?.orderId
  }

  const startIdx = orders.findIndex(order => order.orderId === afterOrderId)
  if (startIdx < 0) {
    return incomplete[0]?.orderId
  }

  for (let offset = 1; offset <= orders.length; offset += 1) {
    const candidate = orders[(startIdx + offset) % orders.length]
    if (candidate && !isOrderAccountedForGroupScan(candidate)) {
      return candidate.orderId
    }
  }

  return undefined
}

export function getScanProgressCounts(order: OrderDetails | undefined): {
  scanned: number
  total: number
} {
  const expected = getExpectedCrateItemsForZebraOrder(order)
  const total = expected.length

  if (total === 0) {
    return { scanned: 0, total: 0 }
  }

  const expectedBarcodes = new Set(expected.map(item => item.barcode))
  const scannedBarcodes = new Set<string>()

  for (const item of order?.crateScan?.scannedBarcodes ?? []) {
    if (expectedBarcodes.has(item.barcode)) {
      scannedBarcodes.add(item.barcode)
    }
  }

  return { scanned: scannedBarcodes.size, total }
}

/** Alerts a scan can warrant (BD-8393, BD-8396). `null` is an ordinary new scan. */
export type ScanAlert = 'wrong-scan' | 'already-scanned' | 'wrong-order' | 'scan-failed'

/**
 * Copy for each scan alert, in one place so it can be revised as a set.
 *
 * Neither is styled as an error: a wrong scan just needs repeating, and a duplicate
 * means the crate is already accounted for. Red read as a fault to fix.
 */
export const SCAN_ALERT_MESSAGES: Record<ScanAlert, string> = {
  'wrong-scan': 'Wrong scan',
  'already-scanned': 'Already scanned!',
  'wrong-order': 'Wrong Order',
  // Placeholder until the approved wording lands. Deliberately says nothing about the
  // underlying failure: the native error text is not fit to show (BD-8393 AC2).
  'scan-failed': 'Couldn’t read that barcode. Please scan again.',
}

/**
 * Which alert a scan for the current order warrants.
 *
 * `scannedBeforeThisScan` is the set of barcodes accounted for *before* this scan
 * arrived - the caller advances it per scan event. Derived scan state cannot stand in
 * for it, because by the time a scan is handled the new barcode is already in there.
 */
export function getScanAlert(
  candidateBarcode: string,
  expectedBarcodes: Set<string>,
  scannedBeforeThisScan: Set<string>,
): ScanAlert | null {
  if (!expectedBarcodes.has(candidateBarcode)) {
    return 'wrong-scan'
  }
  if (scannedBeforeThisScan.has(candidateBarcode)) {
    return 'already-scanned'
  }
  return null
}
